import { supabase } from '@/integrations/supabase/client';

interface SampleRequirementWithTemplate {
  delivery_template_id: string | null;
}

/**
 * Syncs delivery locations from sample/proof requirements to job_delivery_details.
 * This ensures that any address added via the Samples & Proofs section becomes
 * available in the shipment dropdown and is part of the job's delivery locations.
 * 
 * @param jobId - The job ID to sync delivery locations for
 * @param sampleRequirements - Array of sample requirements with delivery_template_id
 * @param userId - The user ID performing the action
 */
export async function syncSampleDeliveryLocations(
  jobId: string,
  sampleRequirements: SampleRequirementWithTemplate[],
  userId: string
): Promise<{ success: boolean; error?: string; addedCount?: number }> {
  try {
    // Get unique template IDs from sample requirements
    const templateIds = [...new Set(
      sampleRequirements
        .map(r => r.delivery_template_id)
        .filter((id): id is string => Boolean(id))
    )];

    if (templateIds.length === 0) {
      return { success: true, addedCount: 0 };
    }

    // Fetch the delivery templates
    const { data: templates, error: templateError } = await supabase
      .from('delivery_location_templates')
      .select('*')
      .in('id', templateIds);

    if (templateError) {
      console.error('Error fetching delivery templates:', templateError);
      return { success: false, error: templateError.message };
    }

    if (!templates || templates.length === 0) {
      return { success: true, addedCount: 0 };
    }

    // Fetch existing job delivery details to check for duplicates
    const { data: existingLocations, error: existingError } = await supabase
      .from('job_delivery_details')
      .select('source_template_id, address_line1, city')
      .eq('job_id', jobId);

    if (existingError) {
      console.error('Error fetching existing delivery locations:', existingError);
      return { success: false, error: existingError.message };
    }

    // Get max delivery_order for new entries
    const { data: maxOrderResult } = await supabase
      .from('job_delivery_details')
      .select('delivery_order')
      .eq('job_id', jobId)
      .order('delivery_order', { ascending: false })
      .limit(1)
      .maybeSingle();

    let nextOrder = (maxOrderResult?.delivery_order ?? 0) + 1;

    // Filter out templates that already exist in job_delivery_details
    const existingTemplateIds = new Set(existingLocations?.map(l => l.source_template_id) || []);
    const existingAddresses = new Set(
      existingLocations?.map(l => `${l.address_line1?.toLowerCase()}-${l.city?.toLowerCase()}`) || []
    );

    const templatesToAdd = templates.filter(template => {
      // Skip if already linked via source_template_id
      if (existingTemplateIds.has(template.id)) {
        return false;
      }
      // Skip if same address already exists (by address + city)
      const addressKey = `${template.address_line1?.toLowerCase()}-${template.city?.toLowerCase()}`;
      if (existingAddresses.has(addressKey)) {
        return false;
      }
      return true;
    });

    if (templatesToAdd.length === 0) {
      return { success: true, addedCount: 0 };
    }

    // Insert new delivery locations from templates (marked as sample-only destinations)
    const deliveryInserts = templatesToAdd.map((template, index) => ({
      job_id: jobId,
      source_template_id: template.id,
      delivery_name: template.location_name,
      location_type: template.location_type || 'other',
      address_line1: template.address_line1,
      address_line2: template.address_line2,
      city: template.city,
      state_province: template.state_province,
      postal_code: template.postal_code,
      country: template.country,
      contact_name: template.contact_name,
      contact_phone: template.contact_phone,
      contact_email: template.contact_email,
      special_instructions: template.special_instructions,
      access_requirements: template.access_requirements,
      delivery_hours: template.delivery_hours,
      preferred_carriers: template.preferred_carriers,
      restricted_carriers: template.restricted_carriers,
      dock_information: template.dock_information,
      created_by_user_id: userId,
      last_updated_by_user_id: userId,
      delivery_order: nextOrder + index,
      deadline_type: 'flexible_date' as const,
      is_product_destination: false,  // Sample locations are NOT product destinations
      is_sample_destination: true,    // Mark as sample destination
    }));

    const { error: insertError } = await supabase
      .from('job_delivery_details')
      .insert(deliveryInserts);

    if (insertError) {
      console.error('Error inserting delivery locations from templates:', insertError);
      return { success: false, error: insertError.message };
    }

    console.log(`✅ Synced ${deliveryInserts.length} sample/proof delivery location(s) to job_delivery_details`);
    return { success: true, addedCount: deliveryInserts.length };
  } catch (error) {
    console.error('Error syncing sample delivery locations:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
